import React, { useCallback } from "react";
import isEqual from 'lodash/isEqual';
import { FormattedMessage, useIntl } from "react-intl";
import { useSelector, useDispatch } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import Button from 'antd/es/button';

import './style.scss'
import { activityLocationActions, datesActions, roomsActions, activityFiltersActions, compareActions } from "@share/store/slices";
import { ActivityLocationsPicker } from "../locations-picker";
import { ActivityRoomPicker } from "../room-picker";
import { FILTERS_LABEL } from "@constants";
import { Routes } from "@share/constants";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import { IActivityLocationsState, IDatesState, IRoomsState } from "@share/store/slices";
import { isDataValid, UrlUtils, CanImpersonate, IsImpersonating, RootState, AppDispatch, useNavigate } from '@share/utils';
import { IAccount } from "@share/common-types";
import { ActivityDatePicker } from '../date-picker';


interface ISearchHotelProps {
  openOnClick: boolean;
  isExperiences?: boolean;
  onSearch?: () => void;
}

export const ActivitySearchForm: React.FC<ISearchHotelProps> = ({ openOnClick, isExperiences, onSearch }) => {
  const intl = useIntl();
  const location = useLocation();
  const navigate = useNavigate();
  const { accountUsername } = useParams<{ accountUsername: string }>();
  const dispatch = useDispatch<AppDispatch>();

  const activityLocationsStore: IActivityLocationsState = useSelector((state: RootState) => state.activityLocationsStore);
  const datesStore: IDatesState = useSelector((state: RootState) => state.datesStore);
  const roomsStore: IRoomsState = useSelector((state: RootState) => state.roomsStore);
  const activityFiltersStore = useSelector((state: RootState) => state.activityFiltersStore);
  const activityStore = useSelector((state: RootState) => state.activityStore);
  const { account } = useSelector((state: RootState) => state.loginStore);

  const { loading } = activityStore;

  // An impersonation-capable agent (e.g. CapitalVacations) must impersonate a
  // member before they can search — while the Impersonate panel is showing and
  // no one is impersonated yet, the search button stays disabled.
  const showImpersonate = CanImpersonate(account as IAccount) && !IsImpersonating();


  /**
   * Leaves the activities landing page for the results page.
   *
   * The results page fills in `locationObject` / `locationLabel` once it mounts, so
   * only the current query has to be carried over here.
   */
  const goToResults = useCallback(() => {
    const searchPath = accountUsername
      ? `/${accountUsername}${Routes.ActivitiesSearch}`
      : Routes.ActivitiesSearch;

    if (location.pathname === searchPath) return;

    navigate(`${searchPath}${window.location.search}`);
  }, [accountUsername, location.pathname, navigate]);

  const handleSearch = useCallback(
    (isInitial = false) => {
      const { isDatesValid, isLocationValid, isValid, isDatesMaxError } = isDataValid(
        activityLocationsStore,
        datesStore,
        { requireDates: false },
      );
      if (!isLocationValid) {
        dispatch(
          activityLocationActions.setError(intl.formatMessage({ id: "location.error" }))
        );
      }
      if (!isDatesValid) {
        dispatch(
          datesActions.setError(
            intl.formatMessage({
              id: isDatesMaxError ? "maximum.date.range.search" : "dates.error",
            })
          )
        );
      }
      if (!isValid) return;
      
      const isLocationChanged = activityLocationsStore.selectedLocation?.code !== activityLocationsStore.location?.code ||
                                activityLocationsStore.selectedLocationLabel !== activityLocationsStore.locationLabel;
      const isDateChanged = datesStore.startDateSelected !== datesStore.startDate;
      
      const rooms1 = (roomsStore.roomsSelected || []).map(r => ({
        adultsCount: r.adultsCount,
        adultAges: [...(r.adultAges || [])].sort(),
        kids: (r.kids || []).map(k => k.age).sort()
      }));
      const rooms2 = (roomsStore.rooms || []).map(r => ({
        adultsCount: r.adultsCount,
        adultAges: [...(r.adultAges || [])].sort(),
        kids: (r.kids || []).map(k => k.age).sort()
      }));

      const isRoomsChanged = !isEqual(rooms1, rooms2);

      if (isLocationChanged || isDateChanged || isRoomsChanged) {
        dispatch(compareActions.setclearActivityCompare());
      }

      dispatch(datesActions.applyDates());
      dispatch(roomsActions.applyRooms());
      dispatch(activityLocationActions.applyLocation());
      dispatch(activityFiltersActions.resetFilters(account?.isCapitalVacations));

      if (!isInitial) {
        UrlUtils.setUrl(FILTERS_LABEL, null);
        goToResults();
      }
      onSearch?.();
    },
    [dispatch, intl, isExperiences, location, activityLocationsStore, datesStore, roomsStore, activityStore, activityFiltersStore, goToResults]
  );

  return (
    <>
      <ActivityLocationsPicker isExperiences={isExperiences} openOnClick={openOnClick} />
      <ActivityDatePicker />
      <ActivityRoomPicker />

      <Button disabled={loading || showImpersonate} className="search-button" onClick={() => handleSearch()} >
        <FontAwesomeIcon icon={faSearch} /> <FormattedMessage id={"search"} />
      </Button>
    </>
  );
};
